import React, { useEffect, useMemo, useState, useRef } from 'react';
import Modal from '../common/Modal';
import styles from './binary.module.scss';
import { Typography } from '../Typography';
import { Button } from '../Button';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { Tooltip } from 'antd';
import TextInput from '../TextInput/TextInput';
import { queueToast } from '../ToastProvider';
import { subscriptionService, SubscriptionPlan } from '../../utils/subscriptionService';

type HostOption = {
  id: string;
  name: string;
  displayName: string;
  icon?: string;
};

type Product = {
  _id: string;
  subscriptionAdditionalHostPrice?: {
    usd: number;
    eur: number;
  };
  subscriptionAdditionalHardwarePrice?: {
    usd: number;
    eur: number;
  };
  additionalHostPrice?: {
    usd: number;
    eur: number;
  };
  additionalHardwarePrice?: {
    usd: number;
    eur: number;
  };
  product?: {
    subscriptionAdditionalHostPrice?: {
      usd: number;
      eur: number;
    };
    subscriptionAdditionalHardwarePrice?: {
      usd: number;
      eur: number;
    };
    additionalHostPrice?: {
      usd: number;
      eur: number;
    };
    additionalHardwarePrice?: {
      usd: number;
      eur: number;
    };
    [key: string]: any;
  };
  [key: string]: any;
};

type SubscriptionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  topOffset?: number;
  productId: string;
  selectedPlanId?: string | null;
};

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1 || '';

// Map app name to icon path
const getIconSrcForApp = (app: string): string => {
  let iconSrc = '';
  switch (app) {
    case 'DaVinci Resolve':
      iconSrc = '/logo/DaVinci_Resolve_Studio.svg';
      break;
    case 'Adobe Premiere Pro':
      iconSrc = '/logo/Adobe_Premiere_Pro.svg';
      break;
    case 'Sony VEGAS Pro':
      iconSrc = '/logo/Vegas_Pro.svg';
      break;
    case 'Final Cut Pro':
      iconSrc = '/logo/Final_Cut_Pro.svg';
      break;
    case 'Natron':
      iconSrc = '/logo/Natron.svg';
      break;
    case 'Nuke':
      iconSrc = '/logo/Nuke.svg';
      break;
    case 'Apple Motion':
      iconSrc = '/logo/Apple_Motion.svg';
      break;
    default:
      iconSrc = '';
  }
  return iconSrc;
};

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  topOffset,
  productId,
  selectedPlanId: externalSelectedPlanId,
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(2);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [hosts, setHosts] = useState<
    Array<{
      _id: string;
      name: string;
      displayName: string;
    }>
  >([]);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [additionalHosts, setAdditionalHosts] = useState<string>('0');
  const [additionalHardware, setAdditionalHardware] = useState<string>('0');
  const [selectedDuration, setSelectedDuration] = useState<number>(1);
  const [currency, setCurrency] = useState<'usd' | 'eur'>('eur');
  const [product, setProduct] = useState<Product | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const previousStepRef = useRef<1 | 2 | 3>(2);

  // Convert hosts from API to HostOption format
  const hostOptions: HostOption[] = useMemo(() => {
    let options: HostOption[] = [];

    hosts.forEach((host) => {
      options.push({
        id: host._id,
        name: host.name,
        displayName: host.displayName,
        icon: getIconSrcForApp(host.displayName),
      });
    });
    // remove duplicates
    options = options.filter(
      (option, index, self) => index === self.findIndex((t) => t.id === option.id)
    );

    return options;
  }, [hosts]);

  // Reset flow when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(2); // Start at step 2 (customization) since plan is already selected
      setHosts([]);
      setSelectedIds([]);
      setSelectedPlan(null);
      setSelectedPlanId(null);
      setAdditionalHosts('0');
      setAdditionalHardware('0');
      setSelectedDuration(1);
      setError(null);
    } else if (isOpen) {
      // When modal opens, go directly to step 2 (customization) - plan selection is removed
      setCurrentStep(2);
    }
  }, [isOpen, externalSelectedPlanId]);

  // Update selectedDuration when plan changes
  useEffect(() => {
    if (selectedPlan?.duration) {
      setSelectedDuration(selectedPlan.duration);
    } else {
      setSelectedDuration(1);
    }
  }, [selectedPlan]);

  // Load hosts when modal opens
  const loadHosts = async () => {
    if (!productId) {
      return;
    }

    setHostsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth-token');
      const response = await axios.get(`${API_BASE_URL}/host?product=${productId}`, {
        headers: {
          Authorization: token ? `${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response?.data?.success && response?.data?.data) {
        let hostsData = [];
        for (const host of response.data.data) {
          if (host.subItems) {
            for (const subItem of Object.keys(host.subItems)) {
              for (const hostItem of host.subItems[subItem]) {
                hostsData.push({
                  _id: hostItem,
                  name: hostItem,
                  displayName: hostItem,
                });
              }
            }
          } else {
            hostsData.push(host);
          }
        }

        setHosts(hostsData);
      }
    } catch (e: any) {
      console.error('Error loading hosts:', e);
      toast.error(e?.response?.data?.message || 'Failed to load available hosts. Please try again.');
    } finally {
      setHostsLoading(false);
    }
  };

  // Load hosts when modal opens
  useEffect(() => {
    if (isOpen && productId) {
      loadHosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productId]);

  // Update internal selectedPlanId when external prop changes
  useEffect(() => {
    if (externalSelectedPlanId) {
      setSelectedPlanId(externalSelectedPlanId);
    }
  }, [externalSelectedPlanId]);

  // Load selected plan details when planId changes
  useEffect(() => {
    if (selectedPlanId) {
      subscriptionService
        .getPlanById(selectedPlanId)
        .then((plan) => {
          setSelectedPlan(plan);
        })
        .catch((error) => {
          console.error('Error loading plan:', error);
          toast.error('Failed to load plan details. Please try again.');
        });
    }
  }, [selectedPlanId]);

  // Fetch product data for pricing
  useEffect(() => {
    if (!productId || !isOpen) return;

    let mounted = true;
    const fetchProduct = async () => {
      setProductLoading(true);
      try {
        let productData = null;
        try {
          const response = await axios.get(`${API_BASE_URL}/products/${productId}`);
          if (response.data?.data) {
            productData = response.data.data;
          } else if (response.data && !response.data.data) {
            productData = response.data;
          }
        } catch (apiError: any) {
          const response = await axios.get(`${API_BASE_URL}/product-details/${productId}`);
          if (response.data?.data) {
            productData = response.data.data;
          }
        }

        if (!mounted) return;

        if (productData) {
          let actualProduct = null;
          if (Array.isArray(productData)) {
            actualProduct = productData[0];
          } else if (productData.product) {
            actualProduct = productData.product;
          } else {
            actualProduct = productData;
          }

          setProduct(actualProduct);
        }
      } catch (e: any) {
        if (!mounted) return;
        console.error('Error fetching product:', e);
        setProduct(null);
        toast.error(
          e?.response?.data?.message || 'Failed to load pricing details. Please try again.'
        );
      } finally {
        if (mounted) {
          setProductLoading(false);
        }
      }
    };

    fetchProduct();
    return () => {
      mounted = false;
    };
  }, [productId, isOpen]);

  // Limit additionalHosts when hosts are loaded
  useEffect(() => {
    if (hostOptions.length > 0 && additionalHosts && selectedPlan) {
      const currentValue = parseInt(additionalHosts);
      const maxAdditionalHosts = Math.max(0, hostOptions.length - selectedPlan.hostLimit);
      if (!isNaN(currentValue) && currentValue > maxAdditionalHosts) {
        setAdditionalHosts(maxAdditionalHosts.toString());
      }
    }
  }, [hostOptions.length, additionalHosts, selectedPlan]);

  // Clear selections when step changes
  useEffect(() => {
    const prevStep = previousStepRef.current;
    if (currentStep === 2 && prevStep === 3 && selectedIds.length > 0) {
      setSelectedIds([]);
    }
    previousStepRef.current = currentStep;
  }, [currentStep, selectedIds.length]);

  // Clear selections when plan or additional hosts count changes
  useEffect(() => {
    if (selectedIds.length > 0) {
      setSelectedIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan?._id, additionalHosts]);

  const toggleSelection = (id: string, maxAllowed: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        if (prev.length < maxAllowed) {
          return [...prev, id];
        }
        return prev;
      }
    });
  };

  // Calculate Step 2 pricing
  const step2Pricing = useMemo(() => {
    if (!selectedPlan) return null;

    const basePlanPrice = selectedPlan.price[currency];

    const additionalHostsCount = additionalHosts ? parseInt(additionalHosts, 10) || 0 : 0;
    const additionalHardwareCount = additionalHardware ? parseInt(additionalHardware, 10) || 0 : 0;

    // Prices per unit (from product API - use subscription-specific prices first)
    const perHostPriceValue =
      product?.subscriptionAdditionalHostPrice?.[currency] ??
      product?.subscriptionAdditionalHostPrice?.eur ??
      product?.product?.subscriptionAdditionalHostPrice?.[currency] ??
      product?.product?.subscriptionAdditionalHostPrice?.eur ??
      product?.additionalHostPrice?.[currency] ??
      product?.additionalHostPrice?.eur ??
      product?.product?.additionalHostPrice?.[currency] ??
      product?.product?.additionalHostPrice?.eur ??
      0;
    const perHardwarePriceValue =
      product?.subscriptionAdditionalHardwarePrice?.[currency] ??
      product?.subscriptionAdditionalHardwarePrice?.eur ??
      product?.product?.subscriptionAdditionalHardwarePrice?.[currency] ??
      product?.product?.subscriptionAdditionalHardwarePrice?.eur ??
      product?.additionalHardwarePrice?.[currency] ??
      product?.additionalHardwarePrice?.eur ??
      product?.product?.additionalHardwarePrice?.[currency] ??
      product?.product?.additionalHardwarePrice?.eur ??
      0;

    // Calculate base costs
    const additionalHostsCost = additionalHostsCount * perHostPriceValue;
    const additionalHardwareCost = additionalHardwareCount * perHardwarePriceValue;

    // Calculate monthly price
    const monthlyPrice = basePlanPrice + additionalHostsCost + additionalHardwareCost;

    // Calculate total price (monthlyPrice × duration)
    const totalPrice = monthlyPrice * selectedDuration;

    return {
      basePlan: {
        price: basePlanPrice,
      },
      additionalHosts: {
        price: additionalHostsCost,
      },
      additionalHardware: {
        price: additionalHardwareCost,
      },
      perHostPrice: perHostPriceValue,
      perHardwarePrice: perHardwarePriceValue,
      monthly: {
        price: monthlyPrice,
      },
      total: {
        price: totalPrice,
      },
    };
  }, [selectedPlan, additionalHosts, additionalHardware, product, currency, selectedDuration]);

  // Handle purchase from step 3
  const handlePurchase = async () => {
    if (!selectedPlanId || !selectedPlan) {
      toast.error('Please select a plan to continue.');
      return;
    }

    const token = localStorage.getItem('auth-token');
    if (!token) {
      localStorage.setItem('redirectTo', window.location.pathname + window.location.search);
      onClose();
      window.location.href = '/login';
      return;
    }

    // Get selected host names
    let selectedHostNames = selectedIds
      .map((id) => {
        const host = hosts.find((h) => h._id === id);
        return host?.name || host?.displayName;
      })
      .filter((name): name is string => !!name);

    // If no hosts selected but all hosts are included in plan, auto-select all
    if (selectedHostNames.length === 0 && selectedPlan && hostOptions.length > 0) {
      const planHostLimit = selectedPlan.hostLimit || 0;
      const additionalHostsCount = parseInt(additionalHosts) || 0;
      const allHostsIncluded = hostOptions.length <= planHostLimit && additionalHostsCount === 0;

      if (allHostsIncluded) {
        // Auto-select all hosts
        selectedHostNames = hostOptions.map((opt) => opt.name || opt.displayName);
        setSelectedIds(hostOptions.map((opt) => opt.id));
      }
    }

    // Validation: supports is required and must have at least one item
    if (selectedHostNames.length === 0) {
      toast.error('Please select at least one host.');
      setError('Please select at least one host.');
      return;
    }

    setPurchaseLoading(true);
    setError(null);

    try {
      const additionalHostsCountFinal = parseInt(additionalHosts) || 0;
      const additionalHardwareCountFinal = parseInt(additionalHardware) || 0;
      const selectedDurationFinal = selectedDuration || selectedPlan.duration || 1;
      const maxWorkflowRunsPerProductFinal = selectedPlan.hardwareLimit || 1;

      const checkout = await subscriptionService.createCheckoutSession(selectedPlanId, currency, {
        additionalHosts: additionalHostsCountFinal,
        additionalHardware: additionalHardwareCountFinal,
        supports: selectedHostNames, // Required field
        selectedDuration: selectedDurationFinal,
        maxWorkflowRunsPerProduct: maxWorkflowRunsPerProductFinal,
        // os is optional - not implemented in UI yet
      });

      // Free plan: subscription is already active, no Stripe redirect needed.
      // window.location.href is a hard navigation that tears down the JS
      // runtime, so the toast is queued in sessionStorage and flushed by
      // <ToastProvider/> once the destination page mounts.
      if (checkout.free) {
        queueToast(
          'success',
          "Subscription activated! We've emailed your confirmation — redirecting you to My Subscriptions."
        );
        window.location.href = '/members-area?tab=mysubscriptions';
      } else if (checkout.url) {
        window.location.href = checkout.url;
      } else {
        toast.error('Something went wrong with the payment server. Please try again.');
      }
    } catch (e: any) {
      console.error('Error creating checkout session:', e);
      const errorMessage =
        e?.response?.data?.message ||
        e?.message ||
        'Failed to create checkout session. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Tooltip icon component for inputs
  const HelpTooltipIcon: React.FC<{ title: React.ReactNode }> = ({ title }) => {
    return (
      <Tooltip
        title={title}
        placement="top"
        overlayInnerStyle={{
          backgroundColor: '#2a2a2a',
          maxWidth: '300px',
          zIndex: 10002,
        }}
        getPopupContainer={() => document.body}
        overlayClassName={styles.tooltipOverlay}
        zIndex={10002}
        destroyTooltipOnHide={true}
      >
        <div
          style={{
            position: 'absolute',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--color-gray-dark-500)',
            pointerEvents: 'auto',
            zIndex: 1,
          }}
        >
          <img src="/help.svg" alt="Help" width="16" height="16" />
        </div>
      </Tooltip>
    );
  };

  // Step 1: Plan Selection - REMOVED: Plans are now selected outside the modal

  // Step 2: Customize
  const renderStep2 = () => {
    if (!selectedPlan) return null;

    const pricing = step2Pricing;

    return (
      <div className={styles.step1Content}>
        {/* Selected Plan Info */}
        <div className={styles.selectedPackageCard}>
          <div className={styles.selectedPackageIcon}>
            <img src="/packageIllustration.svg" alt="Plan" />
          </div>
          <div className={styles.selectedPackageText}>
            <div className={styles.selectedPackageTitleRow}>
              <Typography variant="text-sm" color="grayDark-300" weight="medium">
                Selected plan:
              </Typography>
              <Typography variant="text-sm" color="grayDark-400">
                {selectedPlan.name}
              </Typography>
            </div>
            <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 4 }}>
              Base configuration includes: {selectedPlan.hostLimit} supported hosts,{' '}
              {selectedPlan.hardwareLimit} development machines
            </Typography>
          </div>
        </div>

        {/* Additional Options */}
        <div className={styles.additionalOptions}>
          {/* Add additional hosts */}
          {(() => {
            const maxAdditionalHosts = Math.max(0, hostOptions.length - selectedPlan.hostLimit);
            if (maxAdditionalHosts === 0) {
              return null;
            }
            return (
              <div className={styles.optionField}>
                <Typography variant="text-sm" color="grayDark-300" weight="medium">
                  Number of additional hosts
                </Typography>
                <TextInput
                  placeholder="Enter number of additional hosts"
                  value={additionalHosts}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '') {
                      setAdditionalHosts('');
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        if (maxAdditionalHosts > 0 && numValue > maxAdditionalHosts) {
                          setAdditionalHosts(maxAdditionalHosts.toString());
                        } else {
                          setAdditionalHosts(value);
                        }
                      }
                    }
                  }}
                  fullWidth
                  type="number"
                  max={maxAdditionalHosts > 0 ? maxAdditionalHosts : undefined}
                  helperText={
                    maxAdditionalHosts > 0 && pricing
                      ? `Maximum ${maxAdditionalHosts} additional hosts available (${
                          currency === 'usd' ? '$' : '€'
                        }${productLoading ? '...' : pricing.perHostPrice.toLocaleString()} per host)`
                      : undefined
                  }
                  rightElement={
                    <HelpTooltipIcon
                      title={
                        <div>
                          <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                            Additional Hosts
                          </Typography>
                          <Typography
                            variant="text-sm"
                            color="grayDark-400"
                            style={{ marginTop: 8 }}
                          >
                            Enter the number of additional hosts you need beyond the base plan
                            configuration. Each additional host costs{' '}
                            {currency === 'usd' ? '$' : '€'}
                            {productLoading
                              ? '...'
                              : pricing?.perHostPrice?.toLocaleString() || '0'}{' '}
                            per {selectedPlan.billingCycle === 'yearly' ? 'year' : 'month'}.
                            {maxAdditionalHosts > 0 &&
                              ` Maximum: ${maxAdditionalHosts} additional hosts.`}
                          </Typography>
                        </div>
                      }
                    />
                  }
                />
              </div>
            );
          })()}

          {/* Add additional hardware */}
          <div className={styles.optionField}>
            <Typography variant="text-sm" color="grayDark-300" weight="medium">
              Number of additional development machines
            </Typography>
            <TextInput
              placeholder="Enter number of additional development machines"
              value={additionalHardware}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setAdditionalHardware(value);
              }}
              fullWidth
              type="number"
              rightElement={
                <HelpTooltipIcon
                  title={
                    <div>
                      <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                        Additional Development Machines
                      </Typography>
                      <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 8 }}>
                        Enter the number of additional development machines you need beyond the base
                        plan configuration. Each additional development machine costs{' '}
                        {currency === 'usd' ? '$' : '€'}
                        {productLoading
                          ? '...'
                          : pricing?.perHardwarePrice?.toLocaleString() || '0'}{' '}
                        per {selectedPlan.billingCycle === 'yearly' ? 'year' : 'month'}.
                      </Typography>
                    </div>
                  }
                />
              }
            />
            <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 8 }}>
              {currency === 'usd' ? '$' : '€'}
              {productLoading ? '...' : pricing?.perHardwarePrice?.toLocaleString() || '0'} per
              development machine
            </Typography>
          </div>

          {/* Duration Selection */}
          <div className={styles.optionField}>
            <Typography variant="text-sm" color="grayDark-300" weight="medium">
              Subscription Duration (months)
            </Typography>
            <TextInput
              placeholder="Enter duration in months"
              value={selectedDuration.toString()}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value === '') {
                  setSelectedDuration(1);
                } else {
                  const numValue = parseInt(value);
                  if (!isNaN(numValue)) {
                    if (numValue < 1) {
                      setSelectedDuration(1);
                    } else if (numValue > 12) {
                      setSelectedDuration(12);
                    } else {
                      setSelectedDuration(numValue);
                    }
                  }
                }
              }}
              fullWidth
              type="number"
              min={1}
              max={12}
              rightElement={
                <HelpTooltipIcon
                  title={
                    <div>
                      <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                        Subscription Duration
                      </Typography>
                      <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 8 }}>
                        Select the duration of your subscription. You can choose between 1 to 12
                        months. The total price will be calculated based on the monthly price
                        multiplied by the selected duration.
                      </Typography>
                    </div>
                  }
                />
              }
            />
            <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 8 }}>
              Minimum: 1 month, Maximum: 12 months
            </Typography>
          </div>
        </div>

        {/* Price Summary */}
        {pricing && (
          <div className={styles.priceSummary}>
            <Typography
              variant="text-md"
              color="grayDark-50"
              weight="semibold"
              style={{ marginBottom: 16 }}
            >
              Price Summary
            </Typography>

            {/* Base Plan */}
            <div className={styles.priceRow}>
              <Typography variant="text-sm" color="grayDark-400">
                Base Plan
              </Typography>
              <div className={styles.priceValues}>
                <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                  {currency === 'usd' ? '$' : '€'}
                  {Math.round(pricing.basePlan.price).toLocaleString()}
                </Typography>
              </div>
            </div>

            {/* Additional Hosts */}
            {(() => {
              const maxAdditionalHosts = Math.max(0, hostOptions.length - selectedPlan.hostLimit);
              if (maxAdditionalHosts === 0) {
                return null;
              }
              return (
                <div className={styles.priceRow}>
                  <Typography variant="text-sm" color="grayDark-400">
                    Additional supported hosts ({currency === 'usd' ? '$' : '€'}
                    {productLoading ? '...' : pricing.perHostPrice.toLocaleString()} per host)
                  </Typography>
                  <div className={styles.priceValues}>
                    <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                      {currency === 'usd' ? '$' : '€'}
                      {Math.round(pricing.additionalHosts.price).toLocaleString()}
                    </Typography>
                  </div>
                </div>
              );
            })()}

            {/* Additional Hardware */}
            <div className={styles.priceRow}>
              <Typography variant="text-sm" color="grayDark-400">
                Additional development machines ({currency === 'usd' ? '$' : '€'}
                {productLoading ? '...' : pricing.perHardwarePrice.toLocaleString()} per development
                machine)
              </Typography>
              <div className={styles.priceValues}>
                <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                  {currency === 'usd' ? '$' : '€'}
                  {Math.round(pricing.additionalHardware.price).toLocaleString()}
                </Typography>
              </div>
            </div>

            {/* Monthly Price */}
            <div className={styles.priceRow}>
              <Typography variant="text-sm" color="grayDark-400">
                Monthly Price
              </Typography>
              <div className={styles.priceValues}>
                <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                  {currency === 'usd' ? '$' : '€'}
                  {Math.round(pricing.monthly.price).toLocaleString()}/month
                </Typography>
              </div>
            </div>

            {/* Duration */}
            <div className={styles.priceRow}>
              <Typography variant="text-sm" color="grayDark-400">
                Duration
              </Typography>
              <div className={styles.priceValues}>
                <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                  {selectedDuration} month{selectedDuration !== 1 ? 's' : ''}
                </Typography>
              </div>
            </div>

            {/* Total Price */}
            <div className={styles.priceRowTotal}>
              <Typography variant="text-md" color="grayDark-50" weight="semibold">
                Total price
              </Typography>
              <div className={styles.priceValues}>
                <Typography variant="text-md" color="grayDark-50" weight="semibold">
                  {currency === 'usd' ? '$' : '€'}
                  {Math.round(pricing.total.price).toLocaleString()}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Step 3: Host Selection
  const renderStep3 = () => {
    // Calculate max allowed selections: plan hostLimit + additional hosts
    const planHostLimit = selectedPlan?.hostLimit || 0;
    const additionalHostsCount = parseInt(additionalHosts) || 0;
    const maxAllowedSelections = planHostLimit + additionalHostsCount;

    return (
      <div
        className={purchaseLoading ? styles.contentDisabled : ''}
        aria-disabled={purchaseLoading}
      >
        {error ? <div style={{ marginBottom: 12, color: '#ff6961' }}>{error}</div> : null}
        {maxAllowedSelections > 0 && (
          <Typography variant="text-sm" color="grayDark-400" style={{ marginBottom: 16 }}>
            Select up to {maxAllowedSelections} host{maxAllowedSelections !== 1 ? 's' : ''} (
            {selectedIds.length}/{maxAllowedSelections} selected)
          </Typography>
        )}
        {hostsLoading ? (
          <div className={styles.grid}>
            {[1, 2, 3, 4, 5, 6].map((index) => {
              const widths = [150, 180, 140, 160, 170, 155];
              return (
                <div key={index} className={styles.hostSkeletonCard}>
                  <div className={styles.hostSkeletonLeft}>
                    <div className={styles.hostSkeletonIcon} />
                    <div
                      className={styles.hostSkeletonText}
                      style={{ width: `${widths[index - 1]}px` }}
                    />
                  </div>
                  <div className={styles.hostSkeletonCheck} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.grid}>
            {hostOptions.map((host) => {
              const selected = selectedIds.includes(host.id);
              const isDisabled = !selected && selectedIds.length >= maxAllowedSelections;
              return (
                <button
                  key={host.id}
                  type="button"
                  className={`${styles.hostCard} ${selected ? styles.selected : ''} ${
                    isDisabled ? styles.disabled : ''
                  }`}
                  onClick={() => toggleSelection(host.id, maxAllowedSelections)}
                  aria-pressed={selected}
                  disabled={purchaseLoading || isDisabled}
                >
                  <div className={styles.hostLeft}>
                    {host.icon ? (
                      <img
                        src={host.icon}
                        alt=""
                        aria-hidden="true"
                        style={{ width: '32px', height: '32px' }}
                      />
                    ) : null}
                    <Typography variant="text-lg" color="grayDark-50" weight="medium">
                      {host.displayName}
                    </Typography>
                  </div>
                  <div className={styles.check} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Get title based on current step
  const getModalTitle = () => {
    if (currentStep === 2) {
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 12,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <img
                src="/modal-background-pattern.svg"
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: -24,
                  left: -24,
                  width: 216,
                  height: 217,
                  opacity: 0.9,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
              <img src="/customIllustration.svg" alt="Customize" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', zIndex: 1 }}>
              <Typography variant="text-lg" color="grayDark-50" weight="semibold">
                Customize your subscription
              </Typography>
              <Typography variant="text-sm" color="grayDark-400">
                Configure your subscription plan with additional hosts, machines, and duration.
              </Typography>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 12,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <img
                src="/modal-background-pattern.svg"
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: -24,
                  left: -24,
                  width: 216,
                  height: 217,
                  opacity: 0.9,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
              <img src="/hostIllustration.svg" alt="Host" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', zIndex: 1 }}>
              <Typography variant="text-lg" color="grayDark-50" weight="semibold">
                Select your host
              </Typography>
              <Typography variant="text-sm" color="grayDark-400">
                Choose your preferred host to generate a custom binary license.
              </Typography>
            </div>
          </div>
        </div>
      );
    }
  };

  // Get footer based on current step
  const getModalFooter = () => {
    if (currentStep === 2) {
      // Calculate if we should skip step 3
      const planHostLimit = selectedPlan?.hostLimit || 0;
      const additionalHostsCount = parseInt(additionalHosts) || 0;
      const maxAllowedSelections = planHostLimit + additionalHostsCount;
      const allHostsIncluded =
        selectedPlan &&
        hostOptions.length > 0 &&
        hostOptions.length <= planHostLimit &&
        additionalHostsCount === 0;
      const shouldSkipStep3 = allHostsIncluded || maxAllowedSelections === 0;

      return (
        <div className={styles.footerActions}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (shouldSkipStep3) {
                // If all hosts are included, auto-select all and proceed to purchase
                if (allHostsIncluded && hostOptions.length > 0) {
                  setSelectedIds(hostOptions.map((opt) => opt.id));
                }
                handlePurchase();
              } else {
                setCurrentStep(3);
              }
            }}
            disabled={!selectedPlan}
            loading={shouldSkipStep3 && purchaseLoading}
          >
            {shouldSkipStep3 ? 'Purchase' : 'Continue'}
          </Button>
        </div>
      );
    } else {
      // Step 3 footer
      const planHostLimit = selectedPlan?.hostLimit || 0;
      const additionalHostsCount = parseInt(additionalHosts) || 0;
      const maxAllowedSelections = planHostLimit + additionalHostsCount;

      return (
        <div className={styles.footerActions}>
          <Button variant="secondary" onClick={() => setCurrentStep(2)}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handlePurchase}
            disabled={
              selectedIds.length !== maxAllowedSelections ||
              hostsLoading ||
              hosts.length === 0 ||
              purchaseLoading ||
              maxAllowedSelections === 0 ||
              !selectedPlanId
            }
            loading={purchaseLoading}
          >
            Purchase
          </Button>
        </div>
      );
    }
  };

  return (
    <>
      {/* Toast container with higher z-index than modal - only when modal is open */}
      {isOpen && (
        <Toaster
          position="bottom-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10001,
          }}
          toastOptions={{
            style: {
              zIndex: 10001,
            },
          }}
          gutter={8}
        />
      )}
      <Modal
        isOpen={isOpen}
        onClose={purchaseLoading ? () => {} : onClose}
        topOffset={topOffset}
        maxWidth={880}
        title={getModalTitle()}
        footer={getModalFooter()}
      >
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
      </Modal>
    </>
  );
};

export default SubscriptionModal;
